import React, { useEffect, useState, useCallback } from 'react';
import { FiEdit, FiTrash2, FiPlus, FiAlertTriangle,
         FiCheckCircle, FiSearch, FiImage } from 'react-icons/fi';
import { AdminSidebar } from '../../components/admin';
import { Modal } from '../../components/ui';

const API_BASE = 'https://www.s2i.com.tn';

const getToken = () =>
  localStorage.getItem('token') || sessionStorage.getItem('token') || '';

const getImageUrl = (img) => {
  if (!img) return null;
  return img.startsWith('http') ? img : `${API_BASE}${img}`;
};

const CAT_COLORS = {
  'Serveurs':                { color: '#185FA5', bg: '#E6F1FB' },
  'Stockage':                { color: '#0F6E56', bg: '#E1F5EE' },
  'Switching':               { color: '#854F0B', bg: '#FAEEDA' },
  'Solution HCI':            { color: '#534AB7', bg: '#EEEDFE' },
  'Backup':                  { color: '#A32D2D', bg: '#FCEBEB' },
  'Murs d\'écrans':          { color: '#72243E', bg: '#FBEAF0' },
  'Digital Signage':         { color: '#72243E', bg: '#FBEAF0' },
  'Imprimantes':             { color: '#3B6D11', bg: '#EAF3DE' },
  'Multifonctions':          { color: '#3B6D11', bg: '#EAF3DE' },
  'Impression Production':   { color: '#3B6D11', bg: '#EAF3DE' },
  'Logiciels Impression':    { color: '#444441', bg: '#F1EFE8' },
  'Consommables Impression': { color: '#444441', bg: '#F1EFE8' },
};

const AdminProducts = () => {
  const [products, setProducts]           = useState([]);
  const [categories, setCategories]       = useState([]);
  const [loading, setLoading]             = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType]         = useState('add');
  const [selected, setSelected]           = useState(null);
  const [form, setForm]                   = useState({
    name: '', description: '', categoryId: '', imageUrl: ''
  });
  const [notification, setNotification]   = useState({ show: false, type: '', msg: '' });
  const [searchTerm, setSearchTerm]       = useState('');
  const [filterCat, setFilterCat]         = useState('all');
  const [modalLoading, setModalLoading]   = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [imagePreview, setImagePreview]   = useState(null);

  const token = getToken();

  // ─── Fetch produits ───────────────────────────────────────
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API_BASE}/api/public/products?size=500`);
      const data = await res.json();
      setProducts(Array.isArray(data) ? data : data.content || []);
    } catch {
      showNotify('error', 'Erreur de chargement des produits');
    }
    setLoading(false);
  }, []);

  // ─── Fetch catégories ─────────────────────────────────────
  const fetchCategories = useCallback(async () => {
    try {
      const res  = await fetch(`${API_BASE}/api/public/categories`);
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      console.warn('Catégories non chargées');
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, [fetchProducts, fetchCategories]);

  // ─── Filtrage local ───────────────────────────────────────
  const displayed = products.filter(p => {
    const matchCat    = filterCat === 'all' || p.category?.name === filterCat;
    const matchSearch = !searchTerm
      || p.name?.toLowerCase().includes(searchTerm.toLowerCase())
      || p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      || p.category?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchCat && matchSearch;
  });

  // ─── Helpers ──────────────────────────────────────────────
  const showNotify = (type, msg) => {
    setNotification({ show: true, type, msg });
    setTimeout(() => setNotification({ show: false, type: '', msg: '' }), 3000);
  };

  const handleShowForm = (type, item = null) => {
    setModalType(type);
    setSelected(item);
    setForm(item ? {
      name:        item.name        || '',
      description: item.description || '',
      categoryId:  item.category?.id || '',
      imageUrl:    item.imageUrl    || '',
    } : { name: '', description: '', categoryId: '', imageUrl: '' });
    setImagePreview(item?.imageUrl ? getImageUrl(item.imageUrl) : null);
    setShowFormModal(true);
  };

  // ─── Upload image ─────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', 'products');
    try {
      const res  = await fetch(`${API_BASE}/api/files/upload`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
        body:    fd,
      });
      const path = await res.text();
      setForm(prev => ({ ...prev, imageUrl: path }));
      showNotify('success', 'Image uploadée !');
    } catch {
      showNotify('error', "Échec de l'upload");
    } finally {
      setUploadLoading(false);
    }
  };

  // ─── Save ─────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.categoryId) {
      showNotify('error', 'Le nom et la catégorie sont obligatoires.');
      return;
    }
    setModalLoading(true);
    const payload = {
      name:        form.name.trim(),
      description: form.description.trim(),
      categoryId:  Number(form.categoryId),
      imageUrl:    form.imageUrl || null,
    };
    try {
      const method = modalType === 'add' ? 'POST' : 'PUT';
      const url    = modalType === 'add'
        ? `${API_BASE}/api/products`
        : `${API_BASE}/api/products/${selected.id}`;
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      showNotify('success',
        modalType === 'add' ? 'Produit ajouté !' : 'Produit mis à jour !');
      fetchProducts();
      setShowFormModal(false);
    } catch {
      showNotify('error', "Erreur lors de l'enregistrement");
    } finally {
      setModalLoading(false);
    }
  };

  // ─── Delete ───────────────────────────────────────────────
  const executeDelete = async () => {
    setModalLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/products/${selected.id}`, {
        method:  'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      showNotify('success', 'Produit supprimé !');
      fetchProducts();
      setShowDeleteModal(false);
    } catch {
      showNotify('error', 'Erreur lors de la suppression');
    } finally {
      setModalLoading(false);
    }
  };

  const uniqueCats = [...new Set(products.map(p => p.category?.name).filter(Boolean))].sort();

  // ─── Render ───────────────────────────────────────────────
  return (
    <div className="admin-container">
      <AdminSidebar />
      <div className="admin-content">

        {/* ── Header ── */}
        <div className="admin-header">
          <div>
            <h2>Gestion des Produits</h2>
            <p className="text-muted small">
              {products.length} produit(s) — {displayed.length} affiché(s)
            </p>
          </div>

          <div className="search-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher un produit..."
              className="search-input"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            className="cat-filter"
            value={filterCat}
            onChange={e => setFilterCat(e.target.value)}
          >
            <option value="all">Toutes catégories</option>
            {uniqueCats.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <button
            onClick={() => handleShowForm('add')}
            className="btn-add"
          >
            <FiPlus /> Nouveau Produit
          </button>
        </div>

        {/* ── Stats ── */}
        <div className="stats-row">
          {[
            { label: 'Total produits',  value: products.length,                        color: '#d90000' },
            { label: 'Catégories',      value: uniqueCats.length,                      color: '#185FA5' },
            { label: 'Avec image',      value: products.filter(p => p.imageUrl).length, color: '#0F6E56' },
            { label: 'Sans image',      value: products.filter(p => !p.imageUrl).length,color: '#854F0B' },
          ].map((s, i) => (
            <div key={i} className="stat-card">
              <div className="stat-label">{s.label}</div>
              <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* ── Toast ── */}
        {notification.show && (
          <div className={`toast-sii ${notification.type}`}>
            {notification.type === 'success'
              ? <FiCheckCircle /> : <FiAlertTriangle />}
            {notification.msg}
          </div>
        )}

        {/* ── Table ── */}
        {loading ? (
          <div className="loader-container"><div className="loader" /></div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Description</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.length > 0 ? displayed.map(p => {
                const cat = CAT_COLORS[p.category?.name];
                return (
                  <tr key={p.id}>

                    {/* Image */}
                    <td>
                      {p.imageUrl ? (
                        <img
                          src={getImageUrl(p.imageUrl)}
                          alt={p.name}
                          className="admin-logo-img"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div className="img-placeholder">
                          <FiImage size={20} color="#94a3b8" />
                        </div>
                      )}
                    </td>

                    {/* Nom */}
                    <td>
                      <div className="fw-bold">{p.name}</div>
                    </td>

                    {/* Catégorie */}
                    <td>
                      {cat ? (
                        <span
                          className="cat-badge"
                          style={{ background: cat.bg, color: cat.color }}
                        >
                          {p.category.name}
                        </span>
                      ) : (
                        <span className="cat-badge"
                          style={{ background: '#f1f5f9', color: '#475569' }}>
                          {p.category?.name || '—'}
                        </span>
                      )}
                    </td>

                    {/* Description */}
                    <td>
                      <small className="text-muted">
                        {p.description?.slice(0, 80)}
                        {p.description?.length > 80 ? '…' : ''}
                      </small>
                    </td>

                    {/* Actions */}
                    <td className="text-end">
                      <button
                        onClick={() => handleShowForm('edit', p)}
                        className="btn-icon edit"
                      >
                        <FiEdit />
                      </button>
                      <button
                        onClick={() => { setSelected(p); setShowDeleteModal(true); }}
                        className="btn-icon delete"
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    Aucun produit trouvé.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* ── Modal Formulaire ── */}
        <Modal
          isOpen={showFormModal}
          onClose={() => setShowFormModal(false)}
          title={modalType === 'add' ? 'Ajouter un produit' : 'Modifier le produit'}
        >
          <form onSubmit={handleSubmit} className="admin-form">

            <input
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              placeholder="Nom du produit *"
              required
              className="form-input"
            />

            <select
              value={form.categoryId}
              onChange={e => setForm({ ...form, categoryId: e.target.value })}
              className="form-input"
              required
            >
              <option value="">— Sélectionner une catégorie —</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <textarea
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Description du produit et ses cas d'usage..."
              className="form-input"
              rows="3"
            />

            <div className="upload-box">
              <label>Image du produit :</label>
              <input
                type="file"
                onChange={handleFileChange}
                accept="image/*"
              />
              {uploadLoading && (
                <small style={{ color: '#64748b' }}>Upload en cours...</small>
              )}
            </div>

            {/* URL manuelle */}
            <div className="upload-box">
              <label>Ou entrer l'URL de l'image :</label>
              <input
                value={form.imageUrl}
                onChange={e => {
                  setForm({ ...form, imageUrl: e.target.value });
                  setImagePreview(e.target.value ? getImageUrl(e.target.value) : null);
                }}
                placeholder="/uploads/products/mon-image.png"
                className="form-input"
              />
            </div>

            {/* Preview */}
            {imagePreview && (
              <div className="preview-container">
                <img
                  src={imagePreview}
                  alt="preview"
                  className="preview-img"
                  onError={e => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            <button
              type="submit"
              className="btn-save"
              disabled={modalLoading || uploadLoading}
            >
              {modalLoading ? 'Sauvegarde...' : 'Confirmer'}
            </button>
          </form>
        </Modal>

        {/* ── Modal Suppression ── */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirmation de suppression"
        >
          <div className="text-center p-3">
            <FiAlertTriangle size={50} color="#ef4444" />
            <h4 className="mt-3">Suppression définitive ?</h4>
            <p>
              Voulez-vous supprimer{' '}
              <b style={{ color: '#d90000' }}>{selected?.name}</b> ?
            </p>
            <div className="d-flex justify-content-center gap-3 mt-4">
              <button
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </button>
              <button
                className="btn-confirm-delete"
                onClick={executeDelete}
                disabled={modalLoading}
              >
                Oui, supprimer
              </button>
            </div>
          </div>
        </Modal>

      </div>

      <style>{`
        .admin-container { display:flex; min-height:100vh; background:#f4f7fe; }
        .admin-content { flex:1; padding:40px; position:relative; }
        .admin-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:24px; gap:16px; flex-wrap:wrap; }
        .btn-add { background:#d90000; color:white; border:none; padding:12px 24px; border-radius:10px; cursor:pointer; font-weight:600; display:flex; align-items:center; gap:8px; white-space:nowrap; }
        .search-wrapper { position:relative; flex:1; max-width:350px; }
        .search-icon { position:absolute; left:15px; top:50%; transform:translateY(-50%); color:#94a3b8; }
        .search-input { width:100%; padding:12px 12px 12px 45px; border-radius:25px; border:1px solid #e2e8f0; outline:none; transition:0.3s; }
        .search-input:focus { border-color:#d90000; box-shadow:0 0 0 4px rgba(217,0,0,0.1); }
        .cat-filter { padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; background:white; cursor:pointer; }
        .stats-row { display:flex; gap:16px; flex-wrap:wrap; margin-bottom:24px; }
        .stat-card { background:white; border-radius:12px; padding:16px 20px; flex:1; min-width:120px; box-shadow:0 2px 8px rgba(0,0,0,0.06); }
        .stat-label { font-size:11px; color:#94a3b8; text-transform:uppercase; letter-spacing:.5px; margin-bottom:6px; }
        .stat-value { font-size:26px; font-weight:700; }
        .admin-table { width:100%; background:white; border-radius:12px; border-collapse:collapse; box-shadow:0 4px 12px rgba(0,0,0,0.05); }
        .admin-table td, .admin-table th { padding:15px; border-bottom:1px solid #f1f5f9; }
        .admin-table thead th { font-size:12px; font-weight:600; color:#64748b; text-transform:uppercase; letter-spacing:.4px; background:#f8fafc; }
        .admin-table tbody tr:hover { background:#f8fafc; }
        .admin-table tbody tr:last-child td { border-bottom:none; }
        .admin-logo-img { height:45px; width:70px; object-fit:contain; border-radius:8px; background:#f8fafc; border:1px solid #e9ecef; padding:3px; }
        .img-placeholder { width:70px; height:45px; background:#f1f5f9; border-radius:8px; display:flex; align-items:center; justify-content:center; }
        .cat-badge { padding:4px 12px; border-radius:20px; font-size:12px; font-weight:600; white-space:nowrap; }
        .btn-icon { background:none; border:none; cursor:pointer; font-size:18px; margin-left:8px; padding:8px; border-radius:8px; transition:0.2s; }
        .edit { color:#3b82f6; } .edit:hover { background:#eff6ff; }
        .delete { color:#ef4444; } .delete:hover { background:#fef2f2; }
        .admin-form { display:flex; flex-direction:column; gap:14px; }
        .form-input { padding:12px; border:1px solid #e2e8f0; border-radius:10px; outline:none; width:100%; font-family:inherit; font-size:14px; transition:0.2s; }
        .form-input:focus { border-color:#d90000; box-shadow:0 0 0 3px rgba(217,0,0,0.1); }
        .upload-box { display:flex; flex-direction:column; gap:6px; }
        .upload-box label { font-size:13px; font-weight:600; color:#475569; }
        .preview-container { text-align:center; background:#f8fafc; padding:12px; border-radius:10px; border:1px solid #e9ecef; }
        .preview-img { height:90px; object-fit:contain; border-radius:8px; }
        .btn-save { background:#d90000; color:white; border:none; padding:14px; border-radius:10px; cursor:pointer; font-weight:700; font-size:15px; transition:0.2s; }
        .btn-save:hover { background:#b80000; }
        .btn-save:disabled { opacity:0.6; cursor:not-allowed; }
        .btn-cancel { background:#e2e8f0; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:600; }
        .btn-confirm-delete { background:#ef4444; color:white; border:none; padding:10px 20px; border-radius:8px; cursor:pointer; font-weight:600; }
        .toast-sii { position:fixed; top:20px; right:20px; padding:15px 25px; border-radius:12px; color:white; z-index:9999; display:flex; align-items:center; gap:10px; font-weight:600; box-shadow:0 10px 20px rgba(0,0,0,0.15); }
        .toast-sii.success { background:#10b981; }
        .toast-sii.error { background:#ef4444; }
        .loader { width:40px; height:40px; border:4px solid #f1f5f9; border-top:4px solid #d90000; border-radius:50%; animation:spin 1s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }
        .loader-container { display:flex; justify-content:center; padding:80px; }
      `}</style>
    </div>
  );
};

export default AdminProducts;